import { useState } from 'react';
import { alert, Badge } from '@orgazone/ui-web';
import { api } from './api';
import { useCrossTenant } from './auth';

/**
 * LE BANDEAU « ORGAZONE » — la bande NOIRE en tête d'écran quand un membre du staff agit chez un client.
 *
 * UTILITÉ : « on ne doit jamais oublier où l'on est ni à la place de qui on agit ». C'est la seule chose
 * qui distingue visuellement l'administration de SON back-office de l'administration de celui d'un
 * client — et la confondre, c'est agir dans la base de quelqu'un d'autre en le croyant sien.
 *
 * ⚠️ PLEINE LARGEUR, AU-DESSUS DE LA TOPBAR (recette de la maquette) : glissé DANS la barre du haut, il
 * devenait un élément d'interface parmi d'autres, à la même échelle que le nom du magasin. La bande
 * noire pleine largeur ne peut pas se confondre avec le reste de l'écran — c'est tout son intérêt.
 *
 * ⚠️ SERVI PAR LE SERVEUR : ni le magasin, ni l'identité de l'agent, ni l'échéance ne viennent du
 * navigateur. Un bandeau que le client peut effacer d'un coup d'éditeur d'adresse pendant que l'accès
 * reste ouvert ne prouve rien.
 */
export function StaffStrip() {
  const { ctx, error } = useCrossTenant();
  const [sortie, setSortie] = useState(false);

  // ⚠️ UNE PANNE DE LECTURE SE DIT, elle aussi en bande noire : ne pas savoir au nom de qui la session
  // agit est exactement la situation que ce bandeau existe pour empêcher. Le silence serait pire.
  if (error) {
    return (
      <Bande>
        <StatusPill tone="alerte">CONTEXTE ILLISIBLE</StatusPill>
        <Phrase>
          Impossible de savoir au nom de qui cette session agit : {error}. Ne considérez pas ce
          back-office comme le vôtre tant que ce n’est pas rétabli.
        </Phrase>
      </Bande>
    );
  }
  if (!ctx?.active) return null;

  /**
   * QUITTER — révoque la session, puis retourne à la Console quand le serveur en connaît l'adresse.
   * ⚠️ AUCUNE NAVIGATION SUR ÉCHEC : afficher une sortie qui n'a pas eu lieu laisserait l'accès ouvert
   * pendant que l'écran affirme le contraire (§23.0). L'échec passe par le canal ALERTE, jamais un toast.
   */
  const quitter = async () => {
    setSortie(true);
    try {
      await api.logout();
      // ⚠️ `returnUrl` peut être absent : le back-office ne connaît l'adresse de la Console que si son
      // exploitation la lui a donnée. Sans elle, on retombe sur l'écran de connexion, qui dit à un
      // compte Orgazone que son accès s'ouvre depuis la Console — c'est vrai, et c'est suffisant.
      window.location.assign(ctx.returnUrl ?? '/login');
    } catch (e) {
      setSortie(false);
      await alert({
        title: 'Sortie impossible',
        message: `L'accès n'a pas pu être fermé : ${e instanceof Error ? e.message : String(e)}. Il reste OUVERT, et il se ferme de lui-même à son échéance.`,
        tone: 'err',
      });
    }
  };

  return (
    <Bande>
      <StatusPill tone="marque">ORGAZONE</StatusPill>
      <Phrase>
        Vous êtes dans le back-office de <strong style={{ fontWeight: 700 }}>{ctx.storeName}</strong>, à
        la place du client — chaque geste est journalisé chez lui.
      </Phrase>
      <span style={{ flex: 1 }} />
      {/* L'agent et l'échéance : qui agit, et jusqu'à quand — un accès d'assistance est BORNÉ, et le
          découvrir au milieu d'une opération est le pire moment. */}
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--chrome-dim)', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {ctx.staffEmail}{echeance(ctx.expiresAt)}
      </span>
      <button
        onClick={quitter}
        disabled={sortie}
        title="Ferme cet accès et révoque la session. Il se rouvre depuis la Console, sur le magasin concerné."
        style={{
          height: 22, padding: '0 10px', border: '1px solid var(--chrome-line)', borderRadius: 'var(--r-pill)',
          background: 'transparent', color: 'var(--chrome-ink)', fontSize: 'var(--t-meta)', fontWeight: 600, flexShrink: 0,
          cursor: sortie ? 'progress' : 'pointer', whiteSpace: 'nowrap', opacity: sortie ? 0.6 : 1,
        }}
      >
        {sortie ? 'Fermeture…' : 'Quitter'}
      </button>
    </Bande>
  );
}

/**
 * La bande elle-même : 34 px, pleine largeur — la recette de la maquette.
 * ⚠️ Fond `--chrome-bg` et NON `--ink-950` : l'échelle d'encre s'inverse en thème sombre (`ink-950`
 * y vaut du BLANC), et le back-office démarre en sombre — la bande aurait été blanche sur texte blanc.
 * `--chrome-*` est le rôle ajouté à la charte pour ça : mêmes valeurs dans les deux thèmes, parce
 * qu'un bandeau dont le métier est de rompre avec l'application ne peut pas suivre sa palette.
 */
function Bande({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 11, height: 34, padding: '0 16px',
        background: 'var(--chrome-bg)', color: 'var(--chrome-ink)', fontSize: 'var(--t-dense)', overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

/**
 * La pastille de gauche — teal pour la marque, ambre quand la bande porte une panne.
 * ⚠️ COULEURS IMPOSÉES, et c'est obligatoire ici : la pastille vit dans la bande de contexte, qui ne
 * suit PAS le thème de l'application (jetons `--chrome-*`). Résolues par le thème, ses couleurs
 * basculeraient avec lui — exactement ce que la bande existe pour empêcher.
 */
function StatusPill({ tone, children }: { tone: 'marque' | 'alerte'; children: React.ReactNode }) {
  const marque = tone === 'marque';
  return (
    <Badge
      size="xs"
      colors={marque
        ? { bg: 'var(--teal-500)', fg: '#06231c' }
        : { bg: 'var(--amber)', fg: '#2a1c00' }}
      style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 800, letterSpacing: '.06em' }}
    >{children}</Badge>
  );
}

function Phrase({ children }: { children: React.ReactNode }) {
  return <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>;
}

/** « · fin à 18:42 » — rendu seulement si l'échéance est lisible ; jamais une heure inventée. */
function echeance(iso: string): string {
  const fin = new Date(iso);
  if (Number.isNaN(fin.getTime())) return '';
  return ` · fin à ${fin.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}
